import React, { useContext, useEffect, useState, useRef } from "react";
import { AppContext } from "../../../../appContext";
import { IGoal } from "../../../../types/dashboard";
import { getClientGoals, createGoal, updateGoal, deleteGoal, updateGoalKeepAlive } from "../../../../api/goalApi";
import { GoalCard } from "../../../dashboardPage/components/goalsWidget/GoalCard";
import { GoalModal } from "../../../dashboardPage/components/goalsWidget/GoalModal";
import './MeetingPageGoals.css';

interface IMeetingPageGoalsProps {
    selectedClientId?: string;
    selectedClientName?: string;
}

export const MeetingPageGoals = ({ selectedClientId, selectedClientName }: IMeetingPageGoalsProps) => {
    const { translate } = useContext(AppContext);
    const [goals, setGoals] = useState<IGoal[]>([]);
    const [loading, setLoading] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingGoal, setEditingGoal] = useState<IGoal | null>(null);
    const [error, setError] = useState<string>('');
    
    const saveTimerRef = useRef<NodeJS.Timeout | null>(null);
    const pendingChangesRef = useRef<Map<string, string>>(new Map());

    useEffect(() => {
        if (selectedClientId) {
            loadGoals();
        } else {
            setGoals([]);
            setError('');
        }
    }, [selectedClientId]);

    useEffect(() => {
        const savePendingChangesKeepAlive = () => {
            if (pendingChangesRef.current.size > 0) {
                const changes = Array.from(pendingChangesRef.current.entries());
                pendingChangesRef.current.clear();
                
                for (const [goalId, startDate] of changes) {
                    updateGoalKeepAlive(goalId, { startDate });
                }
            }
        };

        const handleBeforeUnload = () => {
            savePendingChangesKeepAlive();
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }
            savePendingChangesKeepAlive();
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const loadGoals = async () => {
        if (!selectedClientId) return;
        
        setLoading(true);
        setError('');
        try {
            const response = await getClientGoals(selectedClientId);
            setGoals(Array.isArray(response) ? response : []);
            setError('');
        } catch (err) {
            console.error('Failed to load goals:', err);
            const errorMsg = translate('goals_load_error') || 'Failed to load goals';
            setError(errorMsg);
            setGoals([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddGoal = () => {
        setEditingGoal(null);
        setShowModal(true);
    };

    const handleEditGoal = (goal: IGoal) => {
        setEditingGoal(goal);
        setShowModal(true);
    };

    const handleSaveGoal = async (goalData: Partial<IGoal>) => {
        if (!selectedClientId) return;

        try {
            if (editingGoal?.id) {
                await updateGoal(editingGoal.id, goalData);
            } else {
                await createGoal({
                    ...goalData,
                    clientId: selectedClientId
                } as IGoal);
            }
            setError('');
            await loadGoals();
            setShowModal(false);
            setEditingGoal(null);
        } catch (err) {
            console.error('Failed to save goal:', err);
            const errorMsg = translate('goals_save_error') || 'Failed to save goal';
            setError(errorMsg);
        }
    };

    const handleDeleteGoal = async (goalId: string) => {
        try {
            await deleteGoal(goalId);
            setError('');
            await loadGoals();
        } catch (err) {
            console.error('Failed to delete goal:', err);
            const errorMsg = translate('goals_delete_error') || 'Failed to delete goal';
            setError(errorMsg);
        }
    };

    const handleResetGoal = async (goal: IGoal) => {
        try {
            await updateGoal(goal.id!, { startDate: new Date().toISOString() });
            setError('');
            await loadGoals();
        } catch (err) {
            console.error('Failed to reset goal:', err);
            const errorMsg = translate('goals_reset_error') || 'Failed to reset goal';
            setError(errorMsg);
        }
    };

    const handleAdjustDays = (goal: IGoal, newDays: number) => {
        const now = new Date();
        const newStartDate = new Date(now.getTime() - (newDays * 24 * 60 * 60 * 1000));
        const newStartDateISO = newStartDate.toISOString();
        
        setGoals(prevGoals => 
            prevGoals.map(g => 
                g.id === goal.id 
                    ? { ...g, startDate: newStartDateISO }
                    : g
            )
        );
        
        pendingChangesRef.current.set(goal.id!, newStartDateISO);
        
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        
        saveTimerRef.current = setTimeout(async () => {
            if (pendingChangesRef.current.size > 0) {
                const changes = Array.from(pendingChangesRef.current.entries());
                
                for (const [goalId, startDate] of changes) {
                    try {
                        await updateGoal(goalId, { startDate });
                        if (pendingChangesRef.current.get(goalId) === startDate) {
                            pendingChangesRef.current.delete(goalId);
                        }
                        setError('');
                    } catch (err) {
                        console.error('Failed to save goal changes:', err);
                        const errorMsg = translate('goals_update_error') || 'Failed to update goal';
                        setError(errorMsg);
                    }
                }
            }
        }, 10000);
    };

    return (
        <div className={`meetingPageGoals ${isCollapsed ? 'meetingPageGoals--collapsed' : ''}`}>
            <div className="meetingPageGoals_header" onClick={() => setIsCollapsed(!isCollapsed)}>
                <div className="meetingPageGoals_title_wrapper">
                    <div className="meetingPageGoals_title">
                        {translate('goals_title') || 'Goals'}
                    </div>
                </div>
                <div className="meetingPageGoals_toggle">
                    <div className="meetingPageGoals_toggle_icon"></div>
                </div>
            </div>
            <div className="meetingPageGoals_drop">
                <div className="meetingPageGoals_drop_content">
                    {loading ? (
                        <div className="meetingPageGoals_loading">
                            {translate('common_loading') || 'Loading...'}
                        </div>
                    ) : (
                        <div className="meetingPageGoals_content">
                            <div className="meetingPageGoals_grid">
                                {goals.map((goal, index) => (
                                    <GoalCard
                                        key={goal.id}
                                        goal={goal}
                                        colorIndex={index}
                                        onEdit={() => handleEditGoal(goal)}
                                        onDelete={() => handleDeleteGoal(goal.id!)}
                                        onReset={() => handleResetGoal(goal)}
                                        onAdjustDays={(newDays) => handleAdjustDays(goal, newDays)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="meetingPageGoals_actions">
                        <div className="meetingPageGoals_add_wrapper" onClick={handleAddGoal}>
                            <div className="meetingPageGoals_add_icon"></div>
                            <span className="meetingPageGoals_add_text">
                                {translate('goals_add_new') || 'Add a new goal'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            {showModal && (
                <GoalModal
                    goal={editingGoal}
                    onSave={handleSaveGoal}
                    onClose={() => {
                        setShowModal(false);
                        setEditingGoal(null);
                    }}
                />
            )}
        </div>
    );
};
